import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Hammer, Spinner, LockKey, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token] = useState(() => params.get("token") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Reset password · ClickForge";
    // Strip token from browser history so it doesn't linger
    if (token) {
      window.history.replaceState({}, "", "/reset-password");
    }
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) return setError("This reset link is missing a token.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password reset. Redirecting to sign in…");
      setTimeout(() => navigate("/login", { replace: true }), 1600);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-950 text-zinc-100">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
            <Hammer size={20} weight="bold" className="text-zinc-950" />
          </div>
          <div>
            <div className="font-display text-lg font-semibold">ClickForge</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-orange-400">Lead Agent</div>
          </div>
        </div>

        {done ? (
          <div data-testid="reset-done-panel" className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-6 text-center space-y-3">
            <CheckCircle size={40} weight="duotone" className="text-emerald-400 mx-auto" />
            <h2 className="font-display text-xl font-semibold text-zinc-50">Password reset</h2>
            <p className="text-sm text-zinc-400">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-500 mb-2">Set new password</div>
              <h2 className="font-display text-3xl font-semibold text-zinc-50">Choose a new password</h2>
              <p className="text-sm text-zinc-500 mt-1">Minimum 8 characters.</p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form" autoComplete="new-password">
              <div>
                <Label htmlFor="pw" className="text-xs font-mono uppercase tracking-wider text-zinc-500">New password</Label>
                <Input
                  id="pw"
                  data-testid="reset-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoFocus
                  className="mt-1.5 bg-zinc-900 border-zinc-800 focus-visible:border-orange-500 focus-visible:ring-orange-500/30"
                />
              </div>
              <div>
                <Label htmlFor="pw2" className="text-xs font-mono uppercase tracking-wider text-zinc-500">Repeat password</Label>
                <Input
                  id="pw2"
                  data-testid="reset-confirm-input"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  className="mt-1.5 bg-zinc-900 border-zinc-800 focus-visible:border-orange-500 focus-visible:ring-orange-500/30"
                />
              </div>

              {error && (
                <div data-testid="reset-error" className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-sm px-3 py-2">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                data-testid="reset-submit-btn"
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-500 text-zinc-950 font-medium rounded-sm h-10"
              >
                {loading ? (<><Spinner size={16} className="mr-2 animate-spin" /> Setting…</>) : (<><LockKey size={16} className="mr-2" /> Set new password</>)}
              </Button>

              <div className="text-center text-sm text-zinc-500 pt-2">
                <Link to="/login" data-testid="reset-go-login-link" className="text-orange-400 hover:text-orange-300 underline-offset-2 hover:underline">
                  Back to sign in
                </Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
